import { Emulator } from "./emulator.js";
import { createWorkbenchMenuBar } from "./workbench-menu-bar.js";
import type { WorkbenchMenu, WorkbenchMenuBarController } from "./workbench-menu-types.js";
import { actionMenuItem, buttonMenuItem, checkboxMenuItem, menuSeparator, radioMenuItem, selectOptionMenuItem } from "./workbench-menu-utils.js";
import {
  convertSourceImage,
  decodeSourceImage,
  defaultConversionSettings,
  renderSourcePreview,
  type ConversionDitherMode,
  type ConversionFitMode,
  type ConversionPreset,
  type ConversionPaletteStrategy,
  type ConvertedDraftData,
  type ImageConversionSettings,
  type SourceImageState
} from "./image-conversion.js";
import { createHistoryManager, type HistoryManager, type HistoryState } from "./image-editor-history.js";
import { rasterizeFloodFill, rasterizeLine, rasterizeRectangleOutline, type PaintPoint } from "./image-editor-paint-tools.js";

const VISIBLE_WIDTH = 320;
const VISIBLE_HEIGHT = 200;
const BITMAP_SIZE = 8000;
const SCREEN_SIZE = 1000;
const COLOR_SIZE = 500;
const CELL_COLUMNS = 40;
const CELL_ROWS = 25;
const CELL_SIZE = 8;
const PALETTE_SIZE = 16;
const PALETTE_MMIO_BASE = 0xd100;
const VOC_CTRL1_ADDR = 0xd011;
const VOC_CTRL2_ADDR = 0xd016;
const VOC_BORDER_COLOR_ADDR = 0xd020;
const VOC_BG_COLOR_ADDR = 0xd021;
const VOC_CTRL1_BMM_MASK = 0x20;
const VOC_CTRL2_MCM_MASK = 0x10;

type ImageMode = "a" | "b" | "c";
type EditorZoom = 2 | 4 | 8;

type ImageDraft = {
  bgColor: number;
  bitmap: Uint8Array;
  borderColor: number;
  brush: number;
  color: Uint8Array;
  dirty: boolean;
  mode: ImageMode;
  palette: Uint16Array;
  screen: Uint8Array;
  selectedX: number;
  selectedY: number;
  sourceLabel: string;
  zoom: EditorZoom;
};

type LiveImageTarget = {
  bank: number;
  bankBase: number;
  bitmapBase: number;
  colorBase: number;
  mode: ImageMode | null;
  screenBase: number;
};

type BrushDefinition = {
  label: string;
  paletteIndex: number;
  value: number;
};

type BrushView = {
  button: HTMLButtonElement;
  label: HTMLSpanElement;
  swatch: HTMLSpanElement;
  value: number;
};

type PaletteButtonView = {
  button: HTMLButtonElement;
  label: HTMLSpanElement;
  swatch: HTMLSpanElement;
  value: number;
};

type ImageProjectFileV1 = {
  bgColor: number;
  bitmap: number[];
  borderColor: number;
  color: number[];
  format: "am8-image-project";
  mode: ImageMode;
  palette: number[];
  screen: number[];
  version: 1;
};

type ConversionState = {
  preview: ConvertedDraftData;
  settings: ImageConversionSettings;
  source: SourceImageState;
};

type ImageTool = "brush" | "fill" | "line" | "rectangle";

type ColorTarget =
  | "mode-a-fg"
  | "mode-a-bg"
  | "mode-b-bg"
  | "mode-b-hi"
  | "mode-b-lo"
  | "mode-b-ram"
  | "border";

type ImageHistoryDraft = {
  draft: ImageDraft;
  label: string;
};

type RenderScope = "full" | "paint" | "selection" | "palette" | "metadata" | "conversion-only";

type RawImageSetMetadataV1 = {
  bgColor: number;
  borderColor: number;
  format: "am8-image-raw-set";
  mode: ImageMode;
  version: 1;
};

export type ImageEditorElements = {
  applyColorsToAllModeATrigger: HTMLButtonElement;
  applyColorsToAllModeBTrigger: HTMLButtonElement;
  bgColorSelect: HTMLSelectElement;
  bgColorSwatch: HTMLButtonElement;
  bitmapBaseMeta: HTMLElement;
  bankMeta: HTMLElement;
  borderColorSelect: HTMLSelectElement;
  borderColorSwatch: HTMLButtonElement;
  brushes: HTMLDivElement;
  byteOutput: HTMLTextAreaElement;
  cellBgSelect: HTMLSelectElement;
  cellBgSwatch: HTMLButtonElement;
  cellFgSelect: HTMLSelectElement;
  cellFgSwatch: HTMLButtonElement;
  colorBaseMeta: HTMLElement;
  colorTargetStatus: HTMLParagraphElement;
  colorNibbleValue: HTMLElement;
  convertApplyTrigger: HTMLButtonElement;
  convertCancelTrigger: HTMLButtonElement;
  convertCropMeta: HTMLParagraphElement;
  convertDitherSelect: HTMLSelectElement;
  convertDialog: HTMLDialogElement;
  convertFileInput: HTMLInputElement;
  convertFileMeta: HTMLParagraphElement;
  convertFitSelect: HTMLSelectElement;
  convertManualControls: HTMLElement;
  convertManualPanXInput: HTMLInputElement;
  convertManualPanXValue: HTMLParagraphElement;
  convertManualPanYInput: HTMLInputElement;
  convertManualPanYValue: HTMLParagraphElement;
  convertManualZoomInput: HTMLInputElement;
  convertManualZoomValue: HTMLParagraphElement;
  convertMatteInput: HTMLInputElement;
  convertMatteSwatch: HTMLSpanElement;
  convertModeSelect: HTMLSelectElement;
  convertPresetSelect: HTMLSelectElement;
  convertPaletteStrategySelect: HTMLSelectElement;
  convertPreviewCanvas: HTMLCanvasElement;
  convertResetCropTrigger: HTMLButtonElement;
  convertSourceCanvas: HTMLCanvasElement;
  convertTrigger: HTMLButtonElement;
  convertStatus: HTMLParagraphElement;
  dirtyIndicator: HTMLElement;
  emptyConvertTrigger: HTMLButtonElement;
  emptyNewBlankTrigger: HTMLButtonElement;
  emptyReadActiveTrigger: HTMLButtonElement;
  emptyState: HTMLDivElement;
  editorCanvas: HTMLCanvasElement;
  editorScroller: HTMLDivElement;
  expertHints: HTMLUListElement;
  exportRawSetTrigger: HTMLButtonElement;
  historyStatus: HTMLParagraphElement;
  importRawSetInput: HTMLInputElement;
  importRawSetTrigger: HTMLButtonElement;
  layout: HTMLDivElement;
  leftRail: HTMLButtonElement;
  exportByteTrigger: HTMLButtonElement;
  exportProjectTrigger: HTMLButtonElement;
  importProjectInput: HTMLInputElement;
  importProjectTrigger: HTMLButtonElement;
  modeAFields: HTMLElement;
  modeBFields: HTMLElement;
  modeBHiSelect: HTMLSelectElement;
  modeBHiSwatch: HTMLButtonElement;
  modeBLoSelect: HTMLSelectElement;
  modeBLoSwatch: HTMLButtonElement;
  modeBRamSelect: HTMLSelectElement;
  modeBRamSwatch: HTMLButtonElement;
  modeCFields: HTMLElement;
  modeMeta: HTMLElement;
  modeSelect: HTMLSelectElement;
  newBlankTrigger: HTMLButtonElement;
  paletteApplyTrigger: HTMLButtonElement;
  paletteButtons: HTMLDivElement;
  paletteHexInput: HTMLInputElement;
  paletteSelectedMeta: HTMLElement;
  panel: HTMLElement;
  previewCanvas: HTMLCanvasElement;
  readActiveTrigger: HTMLButtonElement;
  redoTrigger: HTMLButtonElement;
  rightRail: HTMLButtonElement;
  screenBaseMeta: HTMLElement;
  screenByteValue: HTMLElement;
  selectedCellMeta: HTMLElement;
  selectedPixelMeta: HTMLElement;
  showCellGridToggle: HTMLInputElement;
  showPixelGridToggle: HTMLInputElement;
  sourceMeta: HTMLElement;
  status: HTMLParagraphElement;
  tools: HTMLDivElement;
  undoTrigger: HTMLButtonElement;
  writeAllTrigger: HTMLButtonElement;
  writeBitmapTrigger: HTMLButtonElement;
  writeColorTrigger: HTMLButtonElement;
  writeHint: HTMLParagraphElement;
  writePaletteTrigger: HTMLButtonElement;
  writeScreenTrigger: HTMLButtonElement;
  zoomSelect: HTMLSelectElement;
};

export type ImageEditorController = {
  refresh(): void;
};

function formatHexByte(value: number): string {
  return (value & 0xff).toString(16).toUpperCase().padStart(2, "0");
}

function formatHexWord(value: number): string {
  return (value & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

function formatHexWordPrefixed(value: number): string {
  return `0x${formatHexWord(value)}`;
}

function formatAsmByte(value: number): string {
  return `$${formatHexByte(value)}`;
}

function formatRgb444(value: number): string {
  return (value & 0x0fff).toString(16).toUpperCase().padStart(3, "0");
}

function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

function modeLabel(mode: ImageMode): string {
  if (mode === "a") {
    return "Mode A";
  }
  if (mode === "b") {
    return "Mode B";
  }
  return "Mode C";
}

function modeDescription(mode: ImageMode): string {
  if (mode === "a") {
    return "320x200 1bpp with per-cell FG/BG colors from Screen-RAM.";
  }
  if (mode === "b") {
    return "160x200 2bpp with BG + Screen-RAM + packed Color-RAM colors.";
  }
  return "160x200 2bpp with direct Palette[0..3] colors.";
}

function toolLabel(tool: ImageTool): string {
  if (tool === "fill") {
    return "Fill";
  }
  if (tool === "line") {
    return "Line";
  }
  if (tool === "rectangle") {
    return "Rectangle";
  }
  return "Brush";
}

function colorTargetLabel(target: ColorTarget): string {
  if (target === "mode-a-fg") {
    return "Mode A FG";
  }
  if (target === "mode-a-bg") {
    return "Mode A BG";
  }
  if (target === "mode-b-bg") {
    return "Mode B BG";
  }
  if (target === "mode-b-hi") {
    return "Mode B Screen Hi";
  }
  if (target === "mode-b-lo") {
    return "Mode B Screen Lo";
  }
  if (target === "mode-b-ram") {
    return "Mode B Color RAM";
  }
  return "Border";
}

function rgb444ToCss(value: number): string {
  const r = ((value >> 8) & 0x0f) * 17;
  const g = ((value >> 4) & 0x0f) * 17;
  const b = (value & 0x0f) * 17;
  return `rgb(${r}, ${g}, ${b})`;
}

function cloneBytes(data: Uint8Array): Uint8Array {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy;
}

function clonePalette(data: Uint16Array): Uint16Array {
  const copy = new Uint16Array(data.length);
  copy.set(data);
  return copy;
}

function logicalWidth(mode: ImageMode): number {
  return mode === "a" ? VISIBLE_WIDTH : 160;
}

function clampBrush(mode: ImageMode, brush: number): number {
  const max = mode === "a" ? 1 : 3;
  return Math.max(0, Math.min(max, brush));
}

function visiblePixelWidth(mode: ImageMode): number {
  return mode === "a" ? 1 : 2;
}

function cellIndexForPoint(mode: ImageMode, x: number, y: number): number {
  const cellX = mode === "a" ? Math.floor(x / 8) : Math.floor(x / 4);
  const cellY = Math.floor(y / 8);
  return cellY * CELL_COLUMNS + cellX;
}

function cellXForPoint(mode: ImageMode, x: number): number {
  return mode === "a" ? Math.floor(x / 8) : Math.floor(x / 4);
}

function bitmapOffsetForPoint(mode: ImageMode, x: number, y: number): number {
  const cellRow = y >> 3;
  const rowInCell = y & 0x07;
  const cellX = mode === "a" ? x >> 3 : x >> 2;
  return cellRow * 320 + cellX * 8 + rowInCell;
}

function getPackedColorNibble(color: Uint8Array, cellIndex: number): number {
  const byteIndex = cellIndex >> 1;
  const value = color[byteIndex] ?? 0;
  return (cellIndex & 1) === 0 ? value & 0x0f : (value >> 4) & 0x0f;
}

function setPackedColorNibble(color: Uint8Array, cellIndex: number, nibble: number): void {
  const byteIndex = cellIndex >> 1;
  const value = color[byteIndex] ?? 0;
  if ((cellIndex & 1) === 0) {
    color[byteIndex] = (value & 0xf0) | (nibble & 0x0f);
    return;
  }
  color[byteIndex] = (value & 0x0f) | ((nibble & 0x0f) << 4);
}

function fillPackedColorNibbles(color: Uint8Array, nibble: number): void {
  color.fill(((nibble & 0x0f) << 4) | (nibble & 0x0f));
}

function seedTargetCellColorsIfBlank(draft: ImageDraft, sourceCellIndex: number, targetCellIndex: number): void {
  if (sourceCellIndex === targetCellIndex) {
    return;
  }

  if (draft.mode === "a") {
    if (screenByteAt(draft, targetCellIndex) === 0) {
      draft.screen[targetCellIndex] = screenByteAt(draft, sourceCellIndex);
    }
    return;
  }

  if (draft.mode !== "b") {
    return;
  }

  if (screenByteAt(draft, targetCellIndex) === 0 && getPackedColorNibble(draft.color, targetCellIndex) === 0) {
    draft.screen[targetCellIndex] = screenByteAt(draft, sourceCellIndex);
    setPackedColorNibble(draft.color, targetCellIndex, getPackedColorNibble(draft.color, sourceCellIndex));
  }
}

function screenByteAt(draft: ImageDraft, cellIndex: number): number {
  return draft.screen[cellIndex] ?? 0;
}

function cellFgColor(draft: ImageDraft, cellIndex: number): number {
  return (screenByteAt(draft, cellIndex) >> 4) & 0x0f;
}

function cellBgColor(draft: ImageDraft, cellIndex: number): number {
  return screenByteAt(draft, cellIndex) & 0x0f;
}

function setCellFgColor(draft: ImageDraft, cellIndex: number, paletteIndex: number): void {
  draft.screen[cellIndex] = ((paletteIndex & 0x0f) << 4) | (screenByteAt(draft, cellIndex) & 0x0f);
}

function setCellBgColor(draft: ImageDraft, cellIndex: number, paletteIndex: number): void {
  draft.screen[cellIndex] = (screenByteAt(draft, cellIndex) & 0xf0) | (paletteIndex & 0x0f);
}

function getPixelCode(draft: ImageDraft, x: number, y: number): number {
  const offset = bitmapOffsetForPoint(draft.mode, x, y);
  const value = draft.bitmap[offset] ?? 0;
  if (draft.mode === "a") {
    return (value & (0x80 >> (x & 0x07))) !== 0 ? 1 : 0;
  }
  return (value >> (6 - ((x & 0x03) * 2))) & 0x03;
}

function setPixelCode(draft: ImageDraft, x: number, y: number, nextCode: number): void {
  const offset = bitmapOffsetForPoint(draft.mode, x, y);
  if (draft.mode === "a") {
    const mask = 0x80 >> (x & 0x07);
    if ((nextCode & 0x01) !== 0) {
      draft.bitmap[offset] |= mask;
      return;
    }
    draft.bitmap[offset] &= ~mask;
    return;
  }

  const shift = 6 - ((x & 0x03) * 2);
  draft.bitmap[offset] = (draft.bitmap[offset] & ~(0x03 << shift)) | ((nextCode & 0x03) << shift);
}

function resolvePaletteIndexAt(draft: ImageDraft, x: number, y: number): number {
  const code = getPixelCode(draft, x, y);
  if (draft.mode === "a") {
    const cellIndex = cellIndexForPoint(draft.mode, x, y);
    return code === 0 ? cellBgColor(draft, cellIndex) : cellFgColor(draft, cellIndex);
  }

  if (draft.mode === "b") {
    const cellIndex = cellIndexForPoint(draft.mode, x, y);
    if (code === 0) {
      return draft.bgColor & 0x0f;
    }
    if (code === 1) {
      return cellFgColor(draft, cellIndex);
    }
    if (code === 2) {
      return cellBgColor(draft, cellIndex);
    }
    return getPackedColorNibble(draft.color, cellIndex);
  }

  return code & 0x03;
}

function buildPaletteBytes(palette: Uint16Array): Uint8Array {
  const bytes = new Uint8Array(PALETTE_SIZE * 2);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const value = palette[index] & 0x0fff;
    bytes[index * 2] = value & 0xff;
    bytes[index * 2 + 1] = (value >> 8) & 0xff;
  }
  return bytes;
}

function parseRgb444(text: string): number {
  const value = text.trim().replace(/^\$/u, "").replace(/^0x/iu, "").replace(/^#/u, "");
  if (!/^[0-9a-fA-F]{1,3}$/u.test(value)) {
    throw new Error("Palette RGB444 values must be 1-3 hexadecimal digits such as 0F3 or ABC.");
  }
  return Number.parseInt(value, 16) & 0x0fff;
}

function projectFromDraft(draft: ImageDraft): ImageProjectFileV1 {
  return {
    bgColor: draft.bgColor & 0x0f,
    bitmap: Array.from(draft.bitmap),
    borderColor: draft.borderColor & 0x0f,
    color: Array.from(draft.color),
    format: "am8-image-project",
    mode: draft.mode,
    palette: Array.from(draft.palette, (value) => value & 0x0fff),
    screen: Array.from(draft.screen),
    version: 1
  };
}

function rawSetMetadataFromDraft(draft: ImageDraft): RawImageSetMetadataV1 {
  return {
    bgColor: draft.bgColor & 0x0f,
    borderColor: draft.borderColor & 0x0f,
    format: "am8-image-raw-set",
    mode: draft.mode,
    version: 1
  };
}

function cloneDraft(draft: ImageDraft): ImageDraft {
  return {
    bgColor: draft.bgColor,
    bitmap: cloneBytes(draft.bitmap),
    borderColor: draft.borderColor,
    brush: draft.brush,
    color: cloneBytes(draft.color),
    dirty: draft.dirty,
    mode: draft.mode,
    palette: clonePalette(draft.palette),
    screen: cloneBytes(draft.screen),
    selectedX: draft.selectedX,
    selectedY: draft.selectedY,
    sourceLabel: draft.sourceLabel,
    zoom: draft.zoom
  };
}

function validateByteArray(name: string, value: unknown, length: number, max: number): Uint8Array {
  if (!Array.isArray(value) || value.length !== length) {
    throw new Error(`${name} must contain exactly ${length} byte values.`);
  }
  const bytes = new Uint8Array(length);
  for (let index = 0; index < length; index += 1) {
    const item = value[index];
    if (!Number.isInteger(item) || item < 0 || item > max) {
      throw new Error(`${name}[${index}] must be an integer in range 0..${max}.`);
    }
    bytes[index] = item;
  }
  return bytes;
}

function validatePaletteArray(value: unknown): Uint16Array {
  if (!Array.isArray(value) || value.length !== PALETTE_SIZE) {
    throw new Error(`palette must contain exactly ${PALETTE_SIZE} RGB444 values.`);
  }
  const palette = new Uint16Array(PALETTE_SIZE);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const item = value[index];
    if (!Number.isInteger(item) || item < 0 || item > 0x0fff) {
      throw new Error(`palette[${index}] must be an integer in range 0..4095.`);
    }
    palette[index] = item;
  }
  return palette;
}

function parseProject(jsonText: string, zoom: EditorZoom): ImageDraft {
  const raw = JSON.parse(jsonText) as Partial<ImageProjectFileV1>;
  if (raw.format !== "am8-image-project" || raw.version !== 1) {
    throw new Error("Image project must use format 'am8-image-project' version 1.");
  }
  if (raw.mode !== "a" && raw.mode !== "b" && raw.mode !== "c") {
    throw new Error("Image project mode must be 'a', 'b', or 'c'.");
  }
  if (typeof raw.bgColor !== "number" || !Number.isInteger(raw.bgColor) || raw.bgColor < 0 || raw.bgColor > 0x0f) {
    throw new Error("Image project bgColor must be in range 0..15.");
  }
  if (typeof raw.borderColor !== "number" || !Number.isInteger(raw.borderColor) || raw.borderColor < 0 || raw.borderColor > 0x0f) {
    throw new Error("Image project borderColor must be in range 0..15.");
  }

  const bitmap = validateByteArray("bitmap", raw.bitmap, BITMAP_SIZE, 0xff);
  const screen = validateByteArray("screen", raw.screen, SCREEN_SIZE, 0xff);
  const color = validateByteArray("color", raw.color, COLOR_SIZE, 0xff);
  const palette = validatePaletteArray(raw.palette);
  const bgColor: number = raw.bgColor;
  const borderColor: number = raw.borderColor;
  const mode: ImageMode = raw.mode;

  return {
    bgColor,
    bitmap,
    borderColor,
    brush: clampBrush(mode, 1),
    color,
    dirty: false,
    mode,
    palette,
    screen,
    selectedX: 0,
    selectedY: 0,
    sourceLabel: "Imported project",
    zoom
  };
}

function parseRawSetMetadata(text: string): RawImageSetMetadataV1 {
  const raw = JSON.parse(text) as Partial<RawImageSetMetadataV1>;
  if (raw.format !== "am8-image-raw-set" || raw.version !== 1) {
    throw new Error("Raw image set metadata must use format 'am8-image-raw-set' version 1.");
  }
  if (raw.mode !== "a" && raw.mode !== "b" && raw.mode !== "c") {
    throw new Error("Raw image set metadata mode must be 'a', 'b', or 'c'.");
  }
  if (typeof raw.bgColor !== "number" || !Number.isInteger(raw.bgColor) || raw.bgColor < 0 || raw.bgColor > 0x0f) {
    throw new Error("Raw image set metadata bgColor must be in range 0..15.");
  }
  if (typeof raw.borderColor !== "number" || !Number.isInteger(raw.borderColor) || raw.borderColor < 0 || raw.borderColor > 0x0f) {
    throw new Error("Raw image set metadata borderColor must be in range 0..15.");
  }
  const bgColor: number = raw.bgColor;
  const borderColor: number = raw.borderColor;
  const mode: ImageMode = raw.mode;
  return {
    bgColor,
    borderColor,
    format: "am8-image-raw-set",
    mode,
    version: 1
  };
}

function paletteFromRawBytes(bytes: Uint8Array): Uint16Array {
  if (bytes.length !== PALETTE_SIZE * 2) {
    throw new Error(`palette raw data must contain exactly ${PALETTE_SIZE * 2} bytes.`);
  }
  const palette = new Uint16Array(PALETTE_SIZE);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const low = bytes[index * 2] ?? 0;
    const high = bytes[index * 2 + 1] ?? 0;
    palette[index] = ((high << 8) | low) & 0x0fff;
  }
  return palette;
}

function downloadText(fileName: string, text: string, mimeType: string): void {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function downloadBytes(fileName: string, bytes: Uint8Array, mimeType: string): void {
  const blob = new Blob([bytes.slice()], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function setSelectOptions(select: HTMLSelectElement, values: number[]): void {
  select.replaceChildren(
    ...values.map((value) => {
      const option = document.createElement("option");
      option.value = value.toString();
      option.textContent = `Palette ${value} (${formatAsmByte(value)})`;
      return option;
    })
  );
}

function createBrushView(): BrushView {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "image-chip";

  const swatch = document.createElement("span");
  swatch.className = "image-chip__swatch";
  button.append(swatch);

  const label = document.createElement("span");
  button.append(label);

  return { button, label, swatch, value: 0 };
}

function createPaletteButtonView(index: number): PaletteButtonView {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "image-palette-button";

  const swatch = document.createElement("span");
  swatch.className = "image-palette-button__swatch";
  button.append(swatch);

  const label = document.createElement("span");
  label.className = "image-palette-button__label";
  label.textContent = index.toString(16).toUpperCase();
  button.append(label);

  return { button, label, swatch, value: index };
}

function canvasPoint(event: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
  const bounds = canvas.getBoundingClientRect();
  return {
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top
  };
}

function isEditableElement(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

function liveTargetFromEmulator(emulator: Emulator): LiveImageTarget {
  const ioc = emulator.getIocState();
  const voc = emulator.getVocState();
  const bank = ioc.videoCtrl & 0x03;
  const bankBase = (bank & 0x03) << 14;
  const screenBase = bankBase + (((voc.memPtr >> 4) & 0x0f) << 10);
  return {
    bank,
    bankBase,
    bitmapBase: bankBase + 0x2000 + ((voc.memPtr & 0x03) << 12),
    colorBase: screenBase + 0x0400,
    mode: ((voc.ctrl1 & VOC_CTRL1_BMM_MASK) === 0) && ((voc.ctrl2 & VOC_CTRL2_MCM_MASK) === 0)
      ? null
      : (voc.ctrl1 & VOC_CTRL1_BMM_MASK) !== 0 && (voc.ctrl2 & VOC_CTRL2_MCM_MASK) === 0
        ? "a"
        : (voc.ctrl1 & VOC_CTRL1_BMM_MASK) !== 0
          ? "b"
          : "c",
    screenBase,
  };
}

function createBlankDraft(
  mode: ImageMode,
  palette: Uint16Array,
  bgColor: number,
  borderColor: number,
  zoom: EditorZoom,
  sourceLabel: string
): ImageDraft {
  return {
    bgColor: bgColor & 0x0f,
    bitmap: new Uint8Array(BITMAP_SIZE),
    borderColor: borderColor & 0x0f,
    brush: clampBrush(mode, 1),
    color: new Uint8Array(COLOR_SIZE),
    dirty: false,
    mode,
    palette: clonePalette(palette),
    screen: new Uint8Array(SCREEN_SIZE),
    selectedX: 0,
    selectedY: 0,
    sourceLabel,
    zoom
  };
}

function buildByteBlock(label: string, bytes: Uint8Array, bytesPerLine: number): string {
  const lines = [`${label}:`];
  for (let offset = 0; offset < bytes.length; offset += bytesPerLine) {
    const slice = bytes.slice(offset, offset + bytesPerLine);
    lines.push(`  .byte ${Array.from(slice, (value) => formatAsmByte(value)).join(", ")}`);
  }
  return lines.join("\n");
}

function buildCombinedByteOutput(draft: ImageDraft): string {
  const metadata = [
    "; AM8 browser image editor export",
    "image_mode:",
    `  .byte ${formatAsmByte(draft.mode === "a" ? 0 : draft.mode === "b" ? 1 : 2)} ; 0=Mode A, 1=Mode B, 2=Mode C`,
    "image_bg_color:",
    `  .byte ${formatAsmByte(draft.bgColor)}`,
    "image_border_color:",
    `  .byte ${formatAsmByte(draft.borderColor)}`
  ].join("\n");
  return [
    metadata,
    buildByteBlock("image_bitmap", draft.bitmap, 16),
    buildByteBlock("image_screen", draft.screen, 16),
    buildByteBlock("image_color", draft.color, 16),
    buildByteBlock("image_palette", buildPaletteBytes(draft.palette), 16)
  ].join("\n\n");
}

function buildBrushDefinitions(draft: ImageDraft): BrushDefinition[] {
  const cellIndex = cellIndexForPoint(draft.mode, draft.selectedX, draft.selectedY);
  if (draft.mode === "a") {
    return [
      { label: "BG", paletteIndex: cellBgColor(draft, cellIndex), value: 0 },
      { label: "FG", paletteIndex: cellFgColor(draft, cellIndex), value: 1 }
    ];
  }
  if (draft.mode === "b") {
    return [
      { label: "00 BG", paletteIndex: draft.bgColor, value: 0 },
      { label: "01 HI", paletteIndex: cellFgColor(draft, cellIndex), value: 1 },
      { label: "10 LO", paletteIndex: cellBgColor(draft, cellIndex), value: 2 },
      { label: "11 RAM", paletteIndex: getPackedColorNibble(draft.color, cellIndex), value: 3 }
    ];
  }
  return [
    { label: "P0", paletteIndex: 0, value: 0 },
    { label: "P1", paletteIndex: 1, value: 1 },
    { label: "P2", paletteIndex: 2, value: 2 },
    { label: "P3", paletteIndex: 3, value: 3 }
  ];
}

function selectedVisibleX(draft: ImageDraft): number {
  return draft.mode === "a" ? draft.selectedX : draft.selectedX * 2;
}

function selectedVisibleWidth(draft: ImageDraft): number {
  return visiblePixelWidth(draft.mode);
}

function renderDraftRaster(
  context: CanvasRenderingContext2D,
  draft: ImageDraft,
  widthScale: number,
  heightScale: number,
  drawSelection: boolean
): void {
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.imageSmoothingEnabled = false;

  for (let y = 0; y < VISIBLE_HEIGHT; y += 1) {
    for (let x = 0; x < VISIBLE_WIDTH; x += 1) {
      const logicalX = draft.mode === "a" ? x : x >> 1;
      const paletteIndex = resolvePaletteIndexAt(draft, logicalX, y);
      context.fillStyle = rgb444ToCss(draft.palette[paletteIndex & 0x0f] ?? 0);
      context.fillRect(x * widthScale, y * heightScale, widthScale, heightScale);
    }
  }

  if (!drawSelection) {
    return;
  }

  const pixelX = selectedVisibleX(draft) * widthScale;
  const pixelWidth = selectedVisibleWidth(draft) * widthScale;
  const pixelY = draft.selectedY * heightScale;
  const cellVisibleX = cellXForPoint(draft.mode, draft.selectedX) * CELL_SIZE * widthScale;
  const cellVisibleY = Math.floor(draft.selectedY / CELL_SIZE) * CELL_SIZE * heightScale;

  context.save();
  context.strokeStyle = "rgba(99, 212, 200, 0.92)";
  context.lineWidth = Math.max(1, widthScale * 0.2);
  context.strokeRect(cellVisibleX + 0.5, cellVisibleY + 0.5, CELL_SIZE * widthScale - 1, CELL_SIZE * heightScale - 1);
  context.strokeStyle = "rgba(242, 184, 75, 0.98)";
  context.lineWidth = Math.max(1, widthScale * 0.35);
  context.strokeRect(pixelX + 0.5, pixelY + 0.5, pixelWidth - 1, heightScale - 1);
  context.restore();
}

function drawEditorGrid(
  context: CanvasRenderingContext2D,
  draft: ImageDraft,
  showPixelGrid: boolean,
  showCellGrid: boolean
): void {
  if (!showPixelGrid && !showCellGrid) {
    return;
  }

  const zoom = draft.zoom;
  const visiblePixelCount = VISIBLE_WIDTH;
  context.save();

  if (showPixelGrid) {
    context.strokeStyle = "rgba(255, 255, 255, 0.08)";
    context.lineWidth = 1;
    context.beginPath();
    for (let x = 1; x < visiblePixelCount; x += 1) {
      const offset = x * zoom + 0.5;
      context.moveTo(offset, 0);
      context.lineTo(offset, VISIBLE_HEIGHT * zoom);
    }
    for (let y = 1; y < VISIBLE_HEIGHT; y += 1) {
      const offset = y * zoom + 0.5;
      context.moveTo(0, offset);
      context.lineTo(VISIBLE_WIDTH * zoom, offset);
    }
    context.stroke();
  }

  if (showCellGrid) {
    context.strokeStyle = "rgba(99, 212, 200, 0.4)";
    context.lineWidth = 1.5;
    context.beginPath();
    for (let x = 1; x < CELL_COLUMNS; x += 1) {
      const offset = x * CELL_SIZE * zoom + 0.5;
      context.moveTo(offset, 0);
      context.lineTo(offset, VISIBLE_HEIGHT * zoom);
    }
    for (let y = 1; y < CELL_ROWS; y += 1) {
      const offset = y * CELL_SIZE * zoom + 0.5;
      context.moveTo(0, offset);
      context.lineTo(VISIBLE_WIDTH * zoom, offset);
    }
    context.stroke();
  }

  context.restore();
}

function pointFromCanvasEvent(draft: ImageDraft, canvas: HTMLCanvasElement, event: MouseEvent): { x: number; y: number } | null {
  const point = canvasPoint(event, canvas);
  const bounds = canvas.getBoundingClientRect();
  const visibleX = Math.floor(point.x / (bounds.width / VISIBLE_WIDTH));
  const visibleY = Math.floor(point.y / (bounds.height / VISIBLE_HEIGHT));
  if (visibleX < 0 || visibleX >= VISIBLE_WIDTH || visibleY < 0 || visibleY >= VISIBLE_HEIGHT) {
    return null;
  }
  return {
    x: draft.mode === "a" ? visibleX : visibleX >> 1,
    y: visibleY
  };
}

export function setupImageEditor(emulator: Emulator, elements: ImageEditorElements): ImageEditorController {
  const menuHost = document.getElementById("images-menu-bar");
  if (!(menuHost instanceof HTMLDivElement)) {
    throw new Error("missing image menu bar host");
  }
  const brushViews: BrushView[] = [];
  const paletteViews: PaletteButtonView[] = [];
  const previewContext = elements.previewCanvas.getContext("2d");
  const editorContext = elements.editorCanvas.getContext("2d");
  const convertSourceContext = elements.convertSourceCanvas.getContext("2d");
  const convertPreviewContext = elements.convertPreviewCanvas.getContext("2d");
  const history = createHistoryManager<ImageDraft>(cloneDraft, 64);
  let draft: ImageDraft | null = null;
  let defaultMode: ImageMode = "a";
  let defaultZoom: EditorZoom = 4;
  let activePaletteEntry = 0;
  let activeColorTarget: ColorTarget | null = null;
  let currentTool: ImageTool = "brush";
  let pendingShapeAnchor: PaintPoint | null = null;
  let painting = false;
  let activePaintCode = 0;
  let strokeHistorySnapshot: ImageDraft | null = null;
  let strokeMutated = false;
  let conversionState: ConversionState | null = null;
  let conversionStatusMessage = "Choose a source image to preview the conversion.";
  let scheduledConversionPreviewFrame: number | null = null;
  let scheduledConversionPreviewMessage: string | null = null;
  let leftPanelCollapsed = false;
  let menuBar: WorkbenchMenuBarController | null = null;
  let rightPanelCollapsed = false;

  if (previewContext === null || editorContext === null || convertSourceContext === null || convertPreviewContext === null) {
    throw new Error("Image editor canvases require a 2D context.");
  }

  const setStatus = (message: string): void => {
    elements.status.textContent = message;
  };

  const refreshMenuBar = (): void => {
    menuBar?.refresh();
  };

  const updatePanelRails = (): void => {
    elements.panel.dataset.leftCollapsed = leftPanelCollapsed ? "true" : "false";
    elements.panel.dataset.rightCollapsed = rightPanelCollapsed ? "true" : "false";
    elements.leftRail.setAttribute("aria-expanded", leftPanelCollapsed ? "false" : "true");
    elements.rightRail.setAttribute("aria-expanded", rightPanelCollapsed ? "false" : "true");
    elements.leftRail.textContent = leftPanelCollapsed ? "SHOW" : "HIDE";
    elements.rightRail.textContent = rightPanelCollapsed ? "SHOW" : "HIDE";
    elements.leftRail.title = leftPanelCollapsed ? "Show preview panel" : "Hide preview panel";
    elements.rightRail.title = rightPanelCollapsed ? "Show inspector panel" : "Hide inspector panel";
    elements.leftRail.setAttribute("aria-label", leftPanelCollapsed ? "Show preview panel" : "Hide preview panel");
    elements.rightRail.setAttribute("aria-label", rightPanelCollapsed ? "Show inspector panel" : "Hide inspector panel");
    elements.layout.classList.toggle("is-left-collapsed", leftPanelCollapsed);
    elements.layout.classList.toggle("is-right-collapsed", rightPanelCollapsed);
  };

  const basePalette = (): Uint16Array => {
    if (draft !== null) {
      return clonePalette(draft.palette);
    }
    return clonePalette(emulator.getVocState().palette);
  };

  const baseBgColor = (): number => {
    if (draft !== null) {
      return draft.bgColor & 0x0f;
    }
    return emulator.getVocState().bgColor & 0x0f;
  };

  const baseBorderColor = (): number => {
    if (draft !== null) {
      return draft.borderColor & 0x0f;
    }
    return emulator.getVocState().borderColor & 0x0f;
  };

  const draftOrThrow = (): ImageDraft => {
    if (draft === null) {
      throw new Error("Load or create an image draft first.");
    }
    return draft;
  };

  const confirmDiscardDirtyDraft = (action: string): boolean => {
    if (draft === null || !draft.dirty) {
      return true;
    }
    return window.confirm(`Discard unsaved image edits before ${action}?`);
  };

  const refreshColorSelects = (): void => {
    const selects = [
      elements.cellFgSelect,
      elements.cellBgSelect,
      elements.modeBHiSelect,
      elements.modeBLoSelect,
      elements.modeBRamSelect,
      elements.bgColorSelect,
      elements.borderColorSelect
    ];
    for (const select of selects) {
      if (select.options.length === PALETTE_SIZE) {
        continue;
      }
      setSelectOptions(select, Array.from({ length: PALETTE_SIZE }, (_, index) => index));
    }
  };

  const setActiveColorTarget = (target: ColorTarget | null): void => {
    activeColorTarget = activeColorTarget === target ? null : target;
    if (activeColorTarget === null) {
      setStatus("Palette buttons now select palette entries for direct editing again.");
    } else {
      setStatus(`Palette buttons now apply colors to ${colorTargetLabel(activeColorTarget)}.`);
    }
    renderForScope("palette");
  };

  const applyPaletteIndexToColorTarget = (paletteIndex: number): void => {
    if (activeColorTarget === null) {
      return;
    }
    mutateDraft(`Applied palette ${paletteIndex} to ${colorTargetLabel(activeColorTarget)}.`, (currentDraft) => {
      const cellIndex = cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY);
      if (activeColorTarget === "mode-a-fg" || activeColorTarget === "mode-b-hi") {
        setCellFgColor(currentDraft, cellIndex, paletteIndex);
      } else if (activeColorTarget === "mode-a-bg" || activeColorTarget === "mode-b-lo") {
        setCellBgColor(currentDraft, cellIndex, paletteIndex);
      } else if (activeColorTarget === "mode-b-bg") {
        currentDraft.bgColor = paletteIndex & 0x0f;
      } else if (activeColorTarget === "mode-b-ram") {
        setPackedColorNibble(currentDraft.color, cellIndex, paletteIndex);
      } else {
        currentDraft.borderColor = paletteIndex & 0x0f;
      }
      activePaletteEntry = paletteIndex & 0x0f;
    }, "palette");
  };

  const renderPaletteEntryControls = (): void => {
    const currentDraft = draft;
    elements.paletteButtons.replaceChildren();
    paletteViews.length = 0;
    for (let index = 0; index < PALETTE_SIZE; index += 1) {
      const view = createPaletteButtonView(index);
      view.button.addEventListener("click", () => {
        if (draft !== null && activeColorTarget !== null) {
          applyPaletteIndexToColorTarget(view.value);
          return;
        }
        activePaletteEntry = view.value;
        if (draft !== null) {
          elements.paletteHexInput.value = formatRgb444(draft.palette[activePaletteEntry]);
          renderForScope("palette");
        }
      });
      paletteViews.push(view);
      elements.paletteButtons.append(view.button);
    }
    if (currentDraft !== null) {
      elements.paletteHexInput.value = formatRgb444(currentDraft.palette[activePaletteEntry]);
    }
  };

  const renderBrushes = (): void => {
    elements.brushes.replaceChildren();
    brushViews.length = 0;
    if (draft === null) {
      return;
    }

    for (const definition of buildBrushDefinitions(draft)) {
      const view = createBrushView();
      view.value = definition.value;
      view.label.textContent = definition.label;
      view.swatch.style.background = rgb444ToCss(draft.palette[definition.paletteIndex & 0x0f] ?? 0);
      view.button.classList.toggle("is-active", draft.brush === definition.value);
      view.button.addEventListener("click", () => {
        const currentDraft = draftOrThrow();
        currentDraft.brush = definition.value;
        setStatus(`Selected ${definition.label} brush for ${modeLabel(currentDraft.mode)}.`);
        renderAll();
      });
      brushViews.push(view);
      elements.brushes.append(view.button);
    }
  };

  const renderSelectionAndInspector = (): void => {
    if (draft === null) {
      elements.selectedPixelMeta.textContent = "-";
      elements.selectedCellMeta.textContent = "-";
      elements.screenByteValue.textContent = "-";
      elements.colorNibbleValue.textContent = "-";
      elements.modeAFields.hidden = true;
      elements.modeBFields.hidden = true;
      elements.modeCFields.hidden = false;
      elements.modeCFields.textContent = "Load or create a draft to inspect palette-driven pixels.";
      elements.paletteSelectedMeta.textContent = "-";
      for (const swatch of [
        elements.cellFgSwatch,
        elements.cellBgSwatch,
        elements.modeBHiSwatch,
        elements.modeBLoSwatch,
        elements.modeBRamSwatch,
        elements.bgColorSwatch,
        elements.borderColorSwatch
      ]) {
        swatch.classList.remove("is-active");
        swatch.setAttribute("aria-pressed", "false");
      }
      return;
    }

    const cellIndex = cellIndexForPoint(draft.mode, draft.selectedX, draft.selectedY);
    const cellX = cellXForPoint(draft.mode, draft.selectedX);
    const cellY = Math.floor(draft.selectedY / 8);
    const visibleX = selectedVisibleX(draft);
    const visibleWidth = selectedVisibleWidth(draft);

    if (draft.mode === "a") {
      elements.selectedPixelMeta.textContent = `Logical ${draft.selectedX}, ${draft.selectedY} / visible ${visibleX}`;
    } else {
      elements.selectedPixelMeta.textContent = `Logical ${draft.selectedX}, ${draft.selectedY} / visible ${visibleX}..${visibleX + visibleWidth - 1}`;
    }
    elements.selectedCellMeta.textContent = `Cell ${cellX}, ${cellY} / index ${cellIndex}`;
    elements.screenByteValue.textContent = formatAsmByte(screenByteAt(draft, cellIndex));
    elements.colorNibbleValue.textContent = formatAsmByte(getPackedColorNibble(draft.color, cellIndex));
    elements.paletteSelectedMeta.textContent = `Palette ${activePaletteEntry} = #${formatRgb444(draft.palette[activePaletteEntry])}`;
    elements.modeAFields.hidden = draft.mode !== "a";
    elements.modeBFields.hidden = draft.mode !== "b";
    elements.modeCFields.hidden = draft.mode !== "c";
    if (draft.mode === "c") {
      elements.modeCFields.textContent = "Mode C colors come directly from Palette 0..3. Edit those entries below to recolor the image.";
    }

    elements.cellFgSelect.value = cellFgColor(draft, cellIndex).toString();
    elements.cellBgSelect.value = cellBgColor(draft, cellIndex).toString();
    elements.modeBHiSelect.value = cellFgColor(draft, cellIndex).toString();
    elements.modeBLoSelect.value = cellBgColor(draft, cellIndex).toString();
    elements.modeBRamSelect.value = getPackedColorNibble(draft.color, cellIndex).toString();
    elements.bgColorSelect.value = draft.bgColor.toString();
    elements.borderColorSelect.value = draft.borderColor.toString();

    elements.cellFgSwatch.style.background = rgb444ToCss(draft.palette[cellFgColor(draft, cellIndex)] ?? 0);
    elements.cellBgSwatch.style.background = rgb444ToCss(draft.palette[cellBgColor(draft, cellIndex)] ?? 0);
    elements.modeBHiSwatch.style.background = rgb444ToCss(draft.palette[cellFgColor(draft, cellIndex)] ?? 0);
    elements.modeBLoSwatch.style.background = rgb444ToCss(draft.palette[cellBgColor(draft, cellIndex)] ?? 0);
    elements.modeBRamSwatch.style.background = rgb444ToCss(draft.palette[getPackedColorNibble(draft.color, cellIndex)] ?? 0);
    elements.bgColorSwatch.style.background = rgb444ToCss(draft.palette[draft.bgColor] ?? 0);
    elements.borderColorSwatch.style.background = rgb444ToCss(draft.palette[draft.borderColor] ?? 0);

    elements.cellFgSwatch.classList.toggle("is-active", activeColorTarget === "mode-a-fg");
    elements.cellBgSwatch.classList.toggle("is-active", activeColorTarget === "mode-a-bg");
    elements.bgColorSwatch.classList.toggle("is-active", activeColorTarget === "mode-b-bg");
    elements.modeBHiSwatch.classList.toggle("is-active", activeColorTarget === "mode-b-hi");
    elements.modeBLoSwatch.classList.toggle("is-active", activeColorTarget === "mode-b-lo");
    elements.modeBRamSwatch.classList.toggle("is-active", activeColorTarget === "mode-b-ram");
    elements.borderColorSwatch.classList.toggle("is-active", activeColorTarget === "border");
    elements.cellFgSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-a-fg" ? "true" : "false");
    elements.cellBgSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-a-bg" ? "true" : "false");
    elements.bgColorSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-b-bg" ? "true" : "false");
    elements.modeBHiSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-b-hi" ? "true" : "false");
    elements.modeBLoSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-b-lo" ? "true" : "false");
    elements.modeBRamSwatch.setAttribute("aria-pressed", activeColorTarget === "mode-b-ram" ? "true" : "false");
    elements.borderColorSwatch.setAttribute("aria-pressed", activeColorTarget === "border" ? "true" : "false");

    elements.paletteHexInput.value = formatRgb444(draft.palette[activePaletteEntry]);
  };

  const renderMetadata = (): void => {
    const draftControls = [
      elements.cellFgSelect,
      elements.cellBgSelect,
      elements.modeBHiSelect,
      elements.modeBLoSelect,
      elements.modeBRamSelect,
      elements.bgColorSelect,
      elements.borderColorSelect
    ];
    if (draft === null) {
      elements.applyColorsToAllModeATrigger.disabled = true;
      elements.applyColorsToAllModeBTrigger.disabled = true;
      for (const control of draftControls) {
        control.disabled = true;
      }
      elements.dirtyIndicator.textContent = "No Draft";
      elements.dirtyIndicator.classList.remove("is-dirty");
      elements.sourceMeta.textContent = "-";
      elements.modeMeta.textContent = `${modeLabel(defaultMode)} / ${modeDescription(defaultMode)}`;
      elements.bankMeta.textContent = "-";
      elements.bitmapBaseMeta.textContent = "-";
      elements.screenBaseMeta.textContent = "-";
      elements.colorBaseMeta.textContent = "-";
      elements.byteOutput.value = "";
      elements.writeHint.textContent = "Load or create a draft first.";
      elements.exportByteTrigger.disabled = true;
      elements.exportProjectTrigger.disabled = true;
      elements.exportRawSetTrigger.disabled = true;
      elements.writeAllTrigger.title = "Write the full draft into the emulator.";
      elements.writeBitmapTrigger.title = "Advanced: write only bitmap bytes.";
      elements.writeScreenTrigger.title = "Advanced: write only screen bytes.";
      elements.writeColorTrigger.title = "Advanced: write only packed color bytes.";
      elements.writePaletteTrigger.title = "Advanced: write only palette bytes.";
      elements.writeBitmapTrigger.disabled = true;
      elements.writeScreenTrigger.disabled = true;
      elements.writeColorTrigger.disabled = true;
      elements.writePaletteTrigger.disabled = true;
      elements.writeAllTrigger.disabled = true;
      return;
    }

    elements.applyColorsToAllModeATrigger.disabled = draft.mode !== "a";
    elements.applyColorsToAllModeBTrigger.disabled = draft.mode !== "b";
    for (const control of draftControls) {
      control.disabled = false;
    }
    const target = liveTargetFromEmulator(emulator);
    elements.dirtyIndicator.textContent = draft.dirty ? "Dirty" : "Clean";
    elements.dirtyIndicator.classList.toggle("is-dirty", draft.dirty);
    elements.sourceMeta.textContent = draft.sourceLabel;
    elements.modeMeta.textContent = `${modeLabel(draft.mode)} / ${modeDescription(draft.mode)}`;
    elements.bankMeta.textContent = `Bank ${target.bank} @ ${formatHexWordPrefixed(target.bankBase)}`;
    elements.bitmapBaseMeta.textContent = formatHexWordPrefixed(target.bitmapBase);
    elements.screenBaseMeta.textContent = formatHexWordPrefixed(target.screenBase);
    elements.colorBaseMeta.textContent = formatHexWordPrefixed(target.colorBase);
    elements.byteOutput.value = buildCombinedByteOutput(draft);
    elements.writeHint.textContent = `Write All is the standard live commit. Advanced partial writeback targets bitmap ${formatHexWordPrefixed(target.bitmapBase)}, screen ${formatHexWordPrefixed(target.screenBase)}, color ${formatHexWordPrefixed(target.colorBase)}, and palette ${formatHexWordPrefixed(PALETTE_MMIO_BASE)}.`;
    elements.exportByteTrigger.disabled = false;
    elements.exportProjectTrigger.disabled = false;
    elements.exportRawSetTrigger.disabled = false;
    elements.writeAllTrigger.title = "Write the full draft, including mode/bg/border, to the emulator.";
    elements.writeBitmapTrigger.title = "Advanced: write only bitmap bytes to the current live target.";
    elements.writeScreenTrigger.title = "Advanced: write only screen bytes to the current live target.";
    elements.writeColorTrigger.title = "Advanced: write only packed color bytes to the current live target.";
    elements.writePaletteTrigger.title = "Advanced: write only palette bytes to VOC palette RAM.";
    elements.writeBitmapTrigger.disabled = false;
    elements.writeScreenTrigger.disabled = false;
    elements.writeColorTrigger.disabled = false;
    elements.writePaletteTrigger.disabled = false;
    elements.writeAllTrigger.disabled = false;
  };

  const renderPaletteButtons = (): void => {
    if (paletteViews.length === 0) {
      renderPaletteEntryControls();
    }
    for (const view of paletteViews) {
      const color = draft?.palette[view.value] ?? 0;
      view.swatch.style.background = rgb444ToCss(color);
      view.button.classList.toggle("is-active", draft !== null && activePaletteEntry === view.value);
      view.button.disabled = draft === null;
      view.button.setAttribute("aria-label", `Palette ${view.value}`);
      view.label.textContent = `${view.value.toString(16).toUpperCase()} ${formatRgb444(color)}`;
    }
    elements.paletteApplyTrigger.disabled = draft === null;
    elements.paletteHexInput.disabled = draft === null;
    if (activeColorTarget === null) {
      elements.colorTargetStatus.textContent = "Palette clicks edit palette entries directly.";
    } else {
      elements.colorTargetStatus.textContent = `Palette clicks now apply to ${colorTargetLabel(activeColorTarget)}. Click the same swatch again to leave apply mode.`;
    }
  };

  const renderToolButtons = (): void => {
    elements.tools.replaceChildren();
    for (const tool of ["brush", "fill", "line", "rectangle"] as const) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "image-tool-button";
      button.textContent = toolLabel(tool);
      button.classList.toggle("is-active", currentTool === tool);
      button.addEventListener("click", () => {
        selectTool(tool);
      });
      button.disabled = draft === null;
      elements.tools.append(button);
    }
  };

  const selectTool = (tool: ImageTool): void => {
    currentTool = tool;
    pendingShapeAnchor = null;
    setStatus(`Selected ${toolLabel(tool)} tool.`);
    renderForScope("full");
  };

  const buildMenus = (): WorkbenchMenu[] => [
    {
      id: "file",
      label: "File",
      items: [
        buttonMenuItem("new-blank", elements.newBlankTrigger),
        buttonMenuItem("read-active", elements.readActiveTrigger),
        buttonMenuItem("convert-image", elements.convertTrigger),
        menuSeparator(),
        buttonMenuItem("import-project", elements.importProjectTrigger),
        buttonMenuItem("import-raw-set", elements.importRawSetTrigger),
        menuSeparator(),
        buttonMenuItem("export-project", elements.exportProjectTrigger),
        buttonMenuItem("export-raw-set", elements.exportRawSetTrigger),
        buttonMenuItem("export-byte", elements.exportByteTrigger)
      ]
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        buttonMenuItem("undo", elements.undoTrigger, { shortcut: "Ctrl/Cmd+Z" }),
        buttonMenuItem("redo", elements.redoTrigger, { shortcut: "Ctrl/Cmd+Shift+Z" })
      ]
    },
    {
      id: "view",
      label: "View",
      items: [
        ...Array.from(elements.zoomSelect.options).map((option) =>
          selectOptionMenuItem(`zoom-${option.value}`, elements.zoomSelect, option.value)
        ),
        menuSeparator(),
        checkboxMenuItem("show-pixel-grid", "Show Pixel Grid", elements.showPixelGridToggle),
        checkboxMenuItem("show-cell-grid", "Show Cell Grid", elements.showCellGridToggle),
        menuSeparator(),
        actionMenuItem(
          "toggle-left-panel",
          leftPanelCollapsed ? "Show Preview Panel" : "Hide Preview Panel",
          true,
          () => {
            elements.leftRail.click();
          }
        ),
        actionMenuItem(
          "toggle-right-panel",
          rightPanelCollapsed ? "Show Inspector Panel" : "Hide Inspector Panel",
          true,
          () => {
            elements.rightRail.click();
          }
        )
      ]
    },
    {
      id: "tools",
      label: "Tools",
      items: [
        radioMenuItem("tool-brush", "Brush", currentTool === "brush", draft !== null, () => {
          selectTool("brush");
        }),
        radioMenuItem("tool-fill", "Fill", currentTool === "fill", draft !== null, () => {
          selectTool("fill");
        }),
        radioMenuItem("tool-line", "Line", currentTool === "line", draft !== null, () => {
          selectTool("line");
        }),
        radioMenuItem("tool-rectangle", "Rectangle", currentTool === "rectangle", draft !== null, () => {
          selectTool("rectangle");
        })
      ]
    },
    {
      id: "write",
      label: "Write",
      items: [
        buttonMenuItem("write-bitmap", elements.writeBitmapTrigger),
        buttonMenuItem("write-screen", elements.writeScreenTrigger),
        buttonMenuItem("write-color", elements.writeColorTrigger),
        buttonMenuItem("write-palette", elements.writePaletteTrigger),
        buttonMenuItem("write-all", elements.writeAllTrigger)
      ]
    }
  ];

  const renderHistoryControls = (): void => {
    const state = history.getState();
    elements.undoTrigger.disabled = !state.canUndo;
    elements.redoTrigger.disabled = !state.canRedo;
    elements.undoTrigger.title = state.lastUndoLabel === null ? "Undo" : `Undo ${state.lastUndoLabel}`;
    elements.redoTrigger.title = state.lastRedoLabel === null ? "Redo" : `Redo ${state.lastRedoLabel}`;
    elements.historyStatus.textContent = `Undo ${state.undoDepth}${state.lastUndoLabel !== null ? ` (${state.lastUndoLabel})` : ""} | Redo ${state.redoDepth}${state.lastRedoLabel !== null ? ` (${state.lastRedoLabel})` : ""}`;
  };

  const renderEmptyState = (): void => {
    const empty = draft === null;
    elements.emptyState.hidden = !empty;
    elements.editorScroller.hidden = empty;
  };

  const renderHints = (): void => {
    const items: string[] = [];
    items.push("Shift-click selects a pixel/cell without painting.");
    items.push("Write All is the normal live-commit action; the partial write buttons are advanced tools.");
    if (draft !== null) {
      items.push(`${modeLabel(draft.mode)} colors still come from bitmap plus additional RAM/palette metadata.`);
    }
    if (currentTool === "line" || currentTool === "rectangle") {
      items.push(`${toolLabel(currentTool)} uses two clicks: set a start point, then click an end point.`);
    }
    if (conversionState !== null) {
      items.push("Apply to Draft only updates the local draft; it does not write into the emulator until you choose Write All or an advanced partial write.");
    } else {
      items.push("Convert Image... crops and scales into the visible 320x200 frame before producing a draft.");
    }
    elements.expertHints.replaceChildren(
      ...items.map((item) => {
        const li = document.createElement("li");
        li.textContent = item;
        return li;
      })
    );
  };

  const renderPreview = (): void => {
    previewContext.canvas.width = VISIBLE_WIDTH;
    previewContext.canvas.height = VISIBLE_HEIGHT;
    if (draft === null) {
      previewContext.clearRect(0, 0, VISIBLE_WIDTH, VISIBLE_HEIGHT);
      previewContext.fillStyle = "rgba(229, 239, 232, 0.72)";
      previewContext.font = "14px Iosevka, monospace";
      previewContext.fillText("Load or create a draft to preview the image.", 16, 28);
      return;
    }
    renderDraftRaster(previewContext, draft, 1, 1, true);
  };

  const renderEditor = (): void => {
    if (draft === null) {
      editorContext.canvas.width = 640;
      editorContext.canvas.height = 400;
      editorContext.clearRect(0, 0, editorContext.canvas.width, editorContext.canvas.height);
      editorContext.fillStyle = "rgba(229, 239, 232, 0.72)";
      editorContext.font = "14px Iosevka, monospace";
      editorContext.fillText("Load or create a draft to edit the image.", 16, 28);
      return;
    }

    const width = VISIBLE_WIDTH * draft.zoom;
    const height = VISIBLE_HEIGHT * draft.zoom;
    elements.editorCanvas.width = width;
    elements.editorCanvas.height = height;
    renderDraftRaster(editorContext, draft, draft.zoom, draft.zoom, true);
    drawEditorGrid(editorContext, draft, elements.showPixelGridToggle.checked, elements.showCellGridToggle.checked);
  };

  const syncConversionControls = (): void => {
    const state = conversionState;
    const hasState = state !== null;
    elements.convertApplyTrigger.disabled = !hasState;
    elements.convertResetCropTrigger.disabled = !hasState;
    elements.convertPresetSelect.disabled = !hasState;
    elements.convertModeSelect.disabled = !hasState;
    elements.convertFitSelect.disabled = !hasState;
    elements.convertPaletteStrategySelect.disabled = !hasState;
    elements.convertDitherSelect.disabled = !hasState;
    elements.convertMatteInput.disabled = !hasState;
    elements.convertManualZoomInput.disabled = !hasState || state.settings.fitMode !== "manual";
    elements.convertManualPanXInput.disabled = !hasState || state.settings.fitMode !== "manual";
    elements.convertManualPanYInput.disabled = !hasState || state.settings.fitMode !== "manual";
    elements.convertManualControls.hidden = !hasState || state.settings.fitMode !== "manual";

    if (state === null) {
      elements.convertStatus.textContent = conversionStatusMessage;
      elements.convertFileMeta.textContent = "No source image loaded.";
      elements.convertCropMeta.textContent = "-";
      elements.convertMatteInput.value = "000";
      elements.convertMatteSwatch.style.background = rgb444ToCss(0);
      elements.convertManualZoomValue.textContent = "100%";
      elements.convertManualPanXValue.textContent = "0%";
      elements.convertManualPanYValue.textContent = "0%";
      return;
    }

    elements.convertStatus.textContent = conversionStatusMessage;
    elements.convertPresetSelect.value = state.settings.preset;
    elements.convertModeSelect.value = state.settings.mode;
    elements.convertFitSelect.value = state.settings.fitMode;
    elements.convertPaletteStrategySelect.value = state.settings.paletteStrategy;
    const optimize03Option = Array.from(elements.convertPaletteStrategySelect.options).find((option) => option.value === "optimize-0-3");
    if (optimize03Option !== undefined) {
      optimize03Option.disabled = state.settings.mode !== "c";
    }
    elements.convertDitherSelect.value = state.settings.ditherMode;
    elements.convertMatteInput.value = formatRgb444(state.settings.matteRgb444);
    elements.convertMatteSwatch.style.background = rgb444ToCss(state.settings.matteRgb444);
    elements.convertManualZoomInput.value = Math.round(state.settings.manualZoom * 100).toString();
    elements.convertManualPanXInput.value = Math.round(state.settings.manualPanX * 100).toString();
    elements.convertManualPanYInput.value = Math.round(state.settings.manualPanY * 100).toString();
    elements.convertManualZoomValue.textContent = formatPercent(state.settings.manualZoom * 100);
    elements.convertManualPanXValue.textContent = formatPercent(state.settings.manualPanX * 100);
    elements.convertManualPanYValue.textContent = formatPercent(state.settings.manualPanY * 100);
    elements.convertFileMeta.textContent = `${state.source.fileName} — ${state.source.width}x${state.source.height} ${state.source.mimeType || "image"}`;
    elements.convertCropMeta.textContent = `Crop x=${Math.round(state.preview.cropRect.x)}, y=${Math.round(state.preview.cropRect.y)}, w=${Math.round(state.preview.cropRect.width)}, h=${Math.round(state.preview.cropRect.height)}.`;
  };

  const renderConversionDialog = (): void => {
    syncConversionControls();
    convertSourceContext.clearRect(0, 0, convertSourceContext.canvas.width, convertSourceContext.canvas.height);
    convertPreviewContext.clearRect(0, 0, convertPreviewContext.canvas.width, convertPreviewContext.canvas.height);
    if (conversionState === null) {
      convertSourceContext.fillStyle = "rgba(229, 239, 232, 0.72)";
      convertSourceContext.font = "14px Iosevka, monospace";
      convertSourceContext.fillText("Load a PNG or JPEG to begin conversion.", 16, 28);
      convertPreviewContext.fillStyle = "rgba(229, 239, 232, 0.72)";
      convertPreviewContext.font = "14px Iosevka, monospace";
      convertPreviewContext.fillText("Converted preview appears here.", 16, 28);
      return;
    }

    renderSourcePreview(convertSourceContext, conversionState.source, conversionState.settings);
    const tempDraft: ImageDraft = {
      bgColor: conversionState.preview.bgColor,
      bitmap: conversionState.preview.bitmap,
      borderColor: conversionState.preview.borderColor,
      brush: 0,
      color: conversionState.preview.color,
      dirty: false,
      mode: conversionState.preview.mode,
      palette: conversionState.preview.palette,
      screen: conversionState.preview.screen,
      selectedX: 0,
      selectedY: 0,
      sourceLabel: conversionState.source.fileName,
      zoom: 2
    };
    renderDraftRaster(convertPreviewContext, tempDraft, 1, 1, false);
  };

  const cancelScheduledConversionPreview = (): void => {
    if (scheduledConversionPreviewFrame !== null) {
      window.cancelAnimationFrame(scheduledConversionPreviewFrame);
      scheduledConversionPreviewFrame = null;
    }
    scheduledConversionPreviewMessage = null;
  };

  const closeConversionDialog = (): void => {
    cancelScheduledConversionPreview();
    if (elements.convertDialog.open) {
      elements.convertDialog.close();
    }
    if (conversionState !== null) {
      conversionState.source.image.close();
    }
    conversionState = null;
    conversionStatusMessage = "Choose a source image to preview the conversion.";
    elements.convertFileInput.value = "";
    renderConversionDialog();
  };

  const updateConversionPreview = (message?: string): void => {
    if (conversionState === null) {
      return;
    }
    const settings = conversionState.settings;
    const preview = convertSourceImage(conversionState.source, settings, basePalette(), baseBgColor(), baseBorderColor());
    conversionState = {
      ...conversionState,
      preview,
      settings
    };
    conversionStatusMessage = message ?? `${modeLabel(preview.mode)} preview ready with ${settings.fitMode} fit, ${settings.paletteStrategy} palette strategy, and ${settings.ditherMode} dithering.`;
    renderConversionDialog();
  };

  const flushScheduledConversionPreview = (): void => {
    if (scheduledConversionPreviewFrame === null) {
      return;
    }
    window.cancelAnimationFrame(scheduledConversionPreviewFrame);
    scheduledConversionPreviewFrame = null;
    const message = scheduledConversionPreviewMessage ?? undefined;
    scheduledConversionPreviewMessage = null;
    updateConversionPreview(message);
  };

  const scheduleConversionPreview = (message?: string): void => {
    scheduledConversionPreviewMessage = message ?? scheduledConversionPreviewMessage;
    if (scheduledConversionPreviewFrame !== null) {
      return;
    }
    scheduledConversionPreviewFrame = window.requestAnimationFrame(() => {
      scheduledConversionPreviewFrame = null;
      const scheduledMessage = scheduledConversionPreviewMessage ?? undefined;
      scheduledConversionPreviewMessage = null;
      updateConversionPreview(scheduledMessage);
    });
  };

  const applyConversionSettings = (
    mutator: (settings: ImageConversionSettings) => void,
    previewMode: "immediate" | "scheduled" = "immediate"
  ): void => {
    if (conversionState === null) {
      return;
    }
    mutator(conversionState.settings);
    if (conversionState.settings.mode !== "c" && conversionState.settings.paletteStrategy === "optimize-0-3") {
      conversionState.settings.paletteStrategy = "keep-current";
    }
    if (previewMode === "scheduled") {
      scheduleConversionPreview();
      renderConversionDialog();
      return;
    }
    cancelScheduledConversionPreview();
    updateConversionPreview();
  };

  const openConversionDialogForFile = async (file: File): Promise<void> => {
    cancelScheduledConversionPreview();
    const source = await decodeSourceImage(file);
    if (conversionState !== null) {
      conversionState.source.image.close();
    }
    const mode = draft?.mode ?? defaultMode;
    const matteRgb444 = draft !== null
      ? draft.palette[mode === "c" ? 0 : draft.bgColor & 0x0f] ?? 0
      : emulator.getVocState().palette[mode === "c" ? 0 : (emulator.getVocState().bgColor & 0x0f)] ?? 0;
    const settings = defaultConversionSettings(mode, matteRgb444);
    const preview = convertSourceImage(source, settings, basePalette(), baseBgColor(), baseBorderColor());
    conversionState = {
      preview,
      settings,
      source
    };
    conversionStatusMessage = `${modeLabel(preview.mode)} preview ready with ${settings.fitMode} fit, ${settings.paletteStrategy} palette strategy, and ${settings.ditherMode} dithering.`;
    if (!elements.convertDialog.open) {
      elements.convertDialog.showModal();
    }
    renderConversionDialog();
  };

  const applyConversionPreset = (preset: ConversionPreset): void => {
    applyConversionSettings((settings) => {
      settings.preset = preset;
      if (preset === "photo") {
        settings.mode = "b";
        settings.fitMode = "cover";
        settings.paletteStrategy = "optimize-target";
        settings.ditherMode = "floyd";
      } else if (preset === "pixel-art") {
        settings.mode = "a";
        settings.fitMode = "contain";
        settings.paletteStrategy = "keep-current";
        settings.ditherMode = "none";
      } else if (preset === "logo") {
        settings.mode = "c";
        settings.fitMode = "contain";
        settings.paletteStrategy = "optimize-0-3";
        settings.ditherMode = "ordered";
      }
    });
  };

  const applyConversionToDraft = (): void => {
    if (conversionState === null) {
      return;
    }
    flushScheduledConversionPreview();
    const preview = conversionState.preview;
    replaceDraft(
      {
        bgColor: preview.bgColor & 0x0f,
        bitmap: cloneBytes(preview.bitmap),
        borderColor: preview.borderColor & 0x0f,
        brush: clampBrush(preview.mode, 1),
        color: cloneBytes(preview.color),
        dirty: true,
        mode: preview.mode,
        palette: clonePalette(preview.palette),
        screen: cloneBytes(preview.screen),
        selectedX: 0,
        selectedY: 0,
        sourceLabel: `Converted ${conversionState.source.fileName} (${conversionState.settings.fitMode}, ${modeLabel(preview.mode)})`,
        zoom: defaultZoom
      },
      `Applied converted ${modeLabel(preview.mode)} image from ${conversionState.source.fileName} to the local draft.`,
      { pushUndoLabel: "Apply converted image" }
    );
    closeConversionDialog();
  };

  const renderForScope = (scope: RenderScope): void => {
    updatePanelRails();
    if (scope === "conversion-only") {
      if (elements.convertDialog.open || conversionState !== null) {
        renderConversionDialog();
      }
      return;
    }
    refreshColorSelects();
    if (scope !== "paint" && scope !== "selection") {
      renderHistoryControls();
      renderToolButtons();
      renderEmptyState();
    }
    if (scope === "full") {
      renderMetadata();
      renderSelectionAndInspector();
      renderBrushes();
      renderPaletteButtons();
      renderHints();
      renderPreview();
      renderEditor();
    } else if (scope === "paint") {
      renderMetadata();
      renderSelectionAndInspector();
      renderBrushes();
      renderHints();
      renderPreview();
      renderEditor();
    } else if (scope === "selection") {
      renderSelectionAndInspector();
      renderBrushes();
      renderHints();
      renderPreview();
      renderEditor();
    } else if (scope === "palette") {
      renderMetadata();
      renderSelectionAndInspector();
      renderBrushes();
      renderPaletteButtons();
      renderHints();
      renderPreview();
      renderEditor();
    } else {
      renderMetadata();
      renderSelectionAndInspector();
      renderBrushes();
      renderPaletteButtons();
      renderHints();
      renderPreview();
      renderEditor();
    }
    if (elements.convertDialog.open || conversionState !== null) {
      renderConversionDialog();
    }
    refreshMenuBar();
  };

  const renderAll = (): void => {
    renderForScope("full");
  };

  const replaceDraft = (nextDraft: ImageDraft, message: string, options?: { pushUndoLabel?: string }): void => {
    if (options?.pushUndoLabel !== undefined && draft !== null) {
      history.pushUndoSnapshot(options.pushUndoLabel, draft);
    } else {
      history.clear();
    }
    draft = nextDraft;
    defaultMode = nextDraft.mode;
    defaultZoom = nextDraft.zoom;
    elements.modeSelect.value = nextDraft.mode;
    elements.zoomSelect.value = nextDraft.zoom.toString();
    activePaletteEntry = 0;
    activeColorTarget = null;
    pendingShapeAnchor = null;
    setStatus(message);
    renderForScope("full");
  };

  const loadActive = (): void => {
    if (!confirmDiscardDirtyDraft("reading the active image")) {
      setStatus("Kept the current draft.");
      return;
    }

    const voc = emulator.getVocState();
    const target = liveTargetFromEmulator(emulator);
    if (target.mode === null) {
      throw new Error("Image Editor supports VOC-II Mode A, B, or C only; the current live mode is text.");
    }
    replaceDraft(
      {
        bgColor: voc.bgColor & 0x0f,
        bitmap: emulator.copyMemory(target.bitmapBase, BITMAP_SIZE),
        borderColor: voc.borderColor & 0x0f,
        brush: clampBrush(target.mode, 1),
        color: emulator.copyMemory(target.colorBase, COLOR_SIZE),
        dirty: false,
        mode: target.mode,
        palette: clonePalette(voc.palette),
        screen: emulator.copyMemory(target.screenBase, SCREEN_SIZE),
        selectedX: 0,
        selectedY: 0,
        sourceLabel: `Active ${modeLabel(target.mode)} image from bank ${target.bank}`,
        zoom: defaultZoom
      },
      `Loaded active ${modeLabel(target.mode)} image into the local draft.`
    );
  };

  const newBlankDraft = (): void => {
    if (!confirmDiscardDirtyDraft("creating a blank image")) {
      setStatus("Kept the current draft.");
      return;
    }

    const voc = emulator.getVocState();
    replaceDraft(
      createBlankDraft(defaultMode, voc.palette, voc.bgColor, voc.borderColor, defaultZoom, `Blank ${modeLabel(defaultMode)} image`),
      `Created a blank ${modeLabel(defaultMode)} image draft.`
    );
  };

  const mutateDraft = (message: string, mutation: (currentDraft: ImageDraft) => void, renderScope: RenderScope = "full"): void => {
    const currentDraft = draftOrThrow();
    history.pushUndoSnapshot(message, currentDraft);
    mutation(currentDraft);
    currentDraft.dirty = true;
    pendingShapeAnchor = null;
    setStatus(message);
    renderForScope(renderScope);
    if (renderScope === "paint") {
      renderHistoryControls();
    }
  };

  const setSelectedPoint = (currentDraft: ImageDraft, x: number, y: number): void => {
    currentDraft.selectedX = Math.max(0, Math.min(logicalWidth(currentDraft.mode) - 1, x));
    currentDraft.selectedY = Math.max(0, Math.min(VISIBLE_HEIGHT - 1, y));
  };

  const commitStrokeHistory = (): void => {
    if (strokeHistorySnapshot !== null && strokeMutated) {
      history.pushUndoSnapshot("Paint image pixels", strokeHistorySnapshot);
      renderHistoryControls();
    }
    strokeHistorySnapshot = null;
    strokeMutated = false;
  };

  const applyBrushPaint = (x: number, y: number, message: string, announce = true): void => {
    const currentDraft = draftOrThrow();
    if (strokeHistorySnapshot === null) {
      strokeHistorySnapshot = cloneDraft(currentDraft);
      strokeMutated = false;
    }
    const sourceCellIndex = cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY);
    const targetCellIndex = cellIndexForPoint(currentDraft.mode, x, y);
    seedTargetCellColorsIfBlank(currentDraft, sourceCellIndex, targetCellIndex);
    setSelectedPoint(currentDraft, x, y);
    setPixelCode(currentDraft, currentDraft.selectedX, currentDraft.selectedY, activePaintCode);
    currentDraft.dirty = true;
    strokeMutated = true;
    if (announce) {
      setStatus(message);
    }
    renderForScope("paint");
  };

  const updatePaletteEntry = (): void => {
    mutateDraft(`Updated palette entry ${activePaletteEntry}.`, (currentDraft) => {
      currentDraft.palette[activePaletteEntry] = parseRgb444(elements.paletteHexInput.value);
    }, "palette");
  };

  const applyCurrentCellColorsToAll = (): void => {
    mutateDraft("Applied the current cell colors across the whole image draft.", (currentDraft) => {
      const cellIndex = cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY);
      const screenByte = screenByteAt(currentDraft, cellIndex);
      currentDraft.screen.fill(screenByte);
      if (currentDraft.mode === "b") {
        fillPackedColorNibbles(currentDraft.color, getPackedColorNibble(currentDraft.color, cellIndex));
      }
    });
  };

  const writeBitmap = (): void => {
    const currentDraft = draftOrThrow();
    const target = liveTargetFromEmulator(emulator);
    emulator.writeMemory(target.bitmapBase, currentDraft.bitmap);
    setStatus(`Wrote ${BITMAP_SIZE} bitmap bytes to ${formatHexWordPrefixed(target.bitmapBase)}.`);
    renderAll();
  };

  const writeScreen = (): void => {
    const currentDraft = draftOrThrow();
    const target = liveTargetFromEmulator(emulator);
    emulator.writeMemory(target.screenBase, currentDraft.screen);
    setStatus(`Wrote ${SCREEN_SIZE} screen bytes to ${formatHexWordPrefixed(target.screenBase)}.`);
    renderAll();
  };

  const writeColor = (): void => {
    const currentDraft = draftOrThrow();
    const target = liveTargetFromEmulator(emulator);
    emulator.writeMemory(target.colorBase, currentDraft.color);
    setStatus(`Wrote ${COLOR_SIZE} packed color bytes to ${formatHexWordPrefixed(target.colorBase)}.`);
    renderAll();
  };

  const writePalette = (): void => {
    const currentDraft = draftOrThrow();
    emulator.writeMemory(PALETTE_MMIO_BASE, buildPaletteBytes(currentDraft.palette));
    setStatus(`Wrote ${PALETTE_SIZE} palette entries to ${formatHexWordPrefixed(PALETTE_MMIO_BASE)}.`);
    renderAll();
  };

  const writeAll = (): void => {
    const currentDraft = draftOrThrow();
    const voc = emulator.getVocState();
    const target = liveTargetFromEmulator(emulator);
    let nextCtrl1 = voc.ctrl1 & ~VOC_CTRL1_BMM_MASK;
    let nextCtrl2 = voc.ctrl2 & ~VOC_CTRL2_MCM_MASK;
    if (currentDraft.mode === "a") {
      nextCtrl1 |= VOC_CTRL1_BMM_MASK;
    } else if (currentDraft.mode === "b") {
      nextCtrl1 |= VOC_CTRL1_BMM_MASK;
      nextCtrl2 |= VOC_CTRL2_MCM_MASK;
    } else {
      nextCtrl2 |= VOC_CTRL2_MCM_MASK;
    }

    emulator.writeMemory(VOC_CTRL1_ADDR, Uint8Array.of(nextCtrl1));
    emulator.writeMemory(VOC_CTRL2_ADDR, Uint8Array.of(nextCtrl2));
    emulator.writeMemory(VOC_BG_COLOR_ADDR, Uint8Array.of(currentDraft.bgColor & 0x0f));
    emulator.writeMemory(VOC_BORDER_COLOR_ADDR, Uint8Array.of(currentDraft.borderColor & 0x0f));
    emulator.writeMemory(target.bitmapBase, currentDraft.bitmap);
    emulator.writeMemory(target.screenBase, currentDraft.screen);
    emulator.writeMemory(target.colorBase, currentDraft.color);
    emulator.writeMemory(PALETTE_MMIO_BASE, buildPaletteBytes(currentDraft.palette));
    currentDraft.dirty = false;
    setStatus(`Wrote full ${modeLabel(currentDraft.mode)} image draft to emulator memory and VOC registers.`);
    renderAll();
  };

  const exportProject = (): void => {
    const currentDraft = draftOrThrow();
    downloadText(`image-${currentDraft.mode}.am8img.json`, JSON.stringify(projectFromDraft(currentDraft), null, 2), "application/json");
    setStatus(`Exported ${modeLabel(currentDraft.mode)} project JSON.`);
  };

  const exportRawSet = (): void => {
    const currentDraft = draftOrThrow();
    const baseName = `image-${currentDraft.mode}`;
    downloadBytes(`${baseName}.raw`, currentDraft.bitmap, "application/octet-stream");
    downloadBytes(`${baseName}.scr`, currentDraft.screen, "application/octet-stream");
    downloadBytes(`${baseName}.col`, currentDraft.color, "application/octet-stream");
    downloadBytes(`${baseName}.pal`, buildPaletteBytes(currentDraft.palette), "application/octet-stream");
    downloadText(`${baseName}.meta.json`, JSON.stringify(rawSetMetadataFromDraft(currentDraft), null, 2), "application/json");
    setStatus(`Exported ${modeLabel(currentDraft.mode)} raw image set files.`);
  };

  const exportByte = (): void => {
    const currentDraft = draftOrThrow();
    const output = buildCombinedByteOutput(currentDraft);
    elements.byteOutput.value = output;
    downloadText(`image-${currentDraft.mode}.asm`, output, "text/plain");
    setStatus(`Exported ${modeLabel(currentDraft.mode)} .byte source.`);
  };

  const importProject = async (file: File): Promise<void> => {
    if (!confirmDiscardDirtyDraft(`importing ${file.name}`)) {
      setStatus("Kept the current draft.");
      return;
    }
    const text = await file.text();
    const imported = parseProject(text, defaultZoom);
    imported.sourceLabel = file.name.trim() === "" ? "Imported project" : file.name;
    replaceDraft(imported, `Imported image project from ${file.name || "the selected file"}.`, draft === null ? undefined : { pushUndoLabel: "Import image project" });
  };

  const importRawSet = async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      return;
    }
    if (!confirmDiscardDirtyDraft("importing a raw image set")) {
      setStatus("Kept the current draft.");
      return;
    }

    let rawFile: File | null = null;
    let screenFile: File | null = null;
    let colorFile: File | null = null;
    let paletteFile: File | null = null;
    let metaFile: File | null = null;
    for (const file of files) {
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith(".raw")) {
        rawFile = file;
      } else if (lowerName.endsWith(".scr")) {
        screenFile = file;
      } else if (lowerName.endsWith(".col")) {
        colorFile = file;
      } else if (lowerName.endsWith(".pal")) {
        paletteFile = file;
      } else if (lowerName.endsWith(".meta.json")) {
        metaFile = file;
      }
    }
    if (rawFile === null || screenFile === null || colorFile === null || paletteFile === null || metaFile === null) {
      throw new Error("Raw image set import needs .raw, .scr, .col, .pal, and .meta.json files.");
    }

    const [bitmap, screen, color, paletteBytes, metadataText] = await Promise.all([
      rawFile.arrayBuffer(),
      screenFile.arrayBuffer(),
      colorFile.arrayBuffer(),
      paletteFile.arrayBuffer(),
      metaFile.text()
    ]);
    const rawMetadata = parseRawSetMetadata(metadataText);
    const nextDraft: ImageDraft = {
      bgColor: rawMetadata.bgColor,
      bitmap: validateByteArray("bitmap", Array.from(new Uint8Array(bitmap)), BITMAP_SIZE, 0xff),
      borderColor: rawMetadata.borderColor,
      brush: clampBrush(rawMetadata.mode, 1),
      color: validateByteArray("color", Array.from(new Uint8Array(color)), COLOR_SIZE, 0xff),
      dirty: false,
      mode: rawMetadata.mode,
      palette: paletteFromRawBytes(new Uint8Array(paletteBytes)),
      screen: validateByteArray("screen", Array.from(new Uint8Array(screen)), SCREEN_SIZE, 0xff),
      selectedX: 0,
      selectedY: 0,
      sourceLabel: `Imported raw image set (${metaFile.name})`,
      zoom: defaultZoom
    };
    replaceDraft(nextDraft, `Imported raw image set from ${metaFile.name}.`, draft === null ? undefined : { pushUndoLabel: "Import raw image set" });
  };

  const selectPointWithoutPainting = (x: number, y: number): void => {
    const currentDraft = draftOrThrow();
    setSelectedPoint(currentDraft, x, y);
    setStatus(`Selected ${modeLabel(currentDraft.mode)} pixel ${currentDraft.selectedX}, ${currentDraft.selectedY} without painting.`);
    renderForScope("selection");
  };

  const applyFillAtPoint = (x: number, y: number): void => {
    const currentDraft = draftOrThrow();
    const points = rasterizeFloodFill(
      { x, y },
      logicalWidth(currentDraft.mode),
      VISIBLE_HEIGHT,
      (pointX, pointY) => getPixelCode(currentDraft, pointX, pointY),
      currentDraft.brush
    );
    if (points.length === 0) {
      selectPointWithoutPainting(x, y);
      return;
    }
    mutateDraft(`Flood-filled ${modeLabel(currentDraft.mode)} from ${x}, ${y}.`, (draftToMutate) => {
      for (const point of points) {
        setPixelCode(draftToMutate, point.x, point.y, draftToMutate.brush);
      }
      setSelectedPoint(draftToMutate, x, y);
    }, "paint");
  };

  const applyShapeToolAtPoint = (x: number, y: number): void => {
    if (pendingShapeAnchor === null) {
      pendingShapeAnchor = { x, y };
      selectPointWithoutPainting(x, y);
      setStatus(`${toolLabel(currentTool)} start point set at ${x}, ${y}. Click again to finish the shape.`);
      renderForScope("selection");
      return;
    }
    const anchor = pendingShapeAnchor;
    const shape = currentTool === "line" ? rasterizeLine(anchor, { x, y }) : rasterizeRectangleOutline(anchor, { x, y });
    mutateDraft(`Drew a ${toolLabel(currentTool).toLowerCase()} from ${anchor.x}, ${anchor.y} to ${x}, ${y}.`, (currentDraft) => {
      for (const point of shape) {
        setPixelCode(currentDraft, point.x, point.y, currentDraft.brush);
      }
      setSelectedPoint(currentDraft, x, y);
    }, "paint");
  };

  const applyToolAtPoint = (x: number, y: number): void => {
    if (currentTool === "fill") {
      applyFillAtPoint(x, y);
      return;
    }
    if (currentTool === "line" || currentTool === "rectangle") {
      applyShapeToolAtPoint(x, y);
      return;
    }
    applyBrushPaint(x, y, `Edited ${modeLabel(draftOrThrow().mode)} pixel ${x}, ${y} locally.`, true);
  };

  const undoDraft = (): void => {
    if (draft === null) {
      return;
    }
    commitStrokeHistory();
    const restored = history.undo(draft);
    if (restored === null) {
      return;
    }
    draft = restored;
    defaultMode = restored.mode;
    defaultZoom = restored.zoom;
    elements.modeSelect.value = restored.mode;
    elements.zoomSelect.value = restored.zoom.toString();
    activeColorTarget = null;
    pendingShapeAnchor = null;
    setStatus("Undid the latest image-editor draft change.");
    renderForScope("full");
  };

  const redoDraft = (): void => {
    if (draft === null) {
      return;
    }
    commitStrokeHistory();
    const restored = history.redo(draft);
    if (restored === null) {
      return;
    }
    draft = restored;
    defaultMode = restored.mode;
    defaultZoom = restored.zoom;
    elements.modeSelect.value = restored.mode;
    elements.zoomSelect.value = restored.zoom.toString();
    activeColorTarget = null;
    pendingShapeAnchor = null;
    setStatus("Redid the latest image-editor draft change.");
    renderForScope("full");
  };

  elements.readActiveTrigger.addEventListener("click", () => {
    try {
      loadActive();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.newBlankTrigger.addEventListener("click", () => {
    try {
      newBlankDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.importProjectTrigger.addEventListener("click", () => {
    elements.importProjectInput.click();
  });

  elements.importProjectInput.addEventListener("change", () => {
    const [file] = Array.from(elements.importProjectInput.files ?? []);
    if (file === undefined) {
      return;
    }
    void importProject(file)
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : String(error));
        renderAll();
      })
      .finally(() => {
        elements.importProjectInput.value = "";
      });
  });

  elements.importRawSetTrigger.addEventListener("click", () => {
    elements.importRawSetInput.click();
  });

  elements.importRawSetInput.addEventListener("change", () => {
    const files = Array.from(elements.importRawSetInput.files ?? []);
    void importRawSet(files)
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : String(error));
        renderForScope("full");
      })
      .finally(() => {
        elements.importRawSetInput.value = "";
      });
  });

  elements.exportRawSetTrigger.addEventListener("click", () => {
    try {
      exportRawSet();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.emptyNewBlankTrigger.addEventListener("click", () => {
    try {
      newBlankDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.emptyReadActiveTrigger.addEventListener("click", () => {
    try {
      loadActive();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderForScope("full");
    }
  });

  elements.emptyConvertTrigger.addEventListener("click", () => {
    elements.convertFileInput.click();
  });

  elements.undoTrigger.addEventListener("click", () => {
    undoDraft();
  });

  elements.redoTrigger.addEventListener("click", () => {
    redoDraft();
  });

  elements.convertTrigger.addEventListener("click", () => {
    elements.convertFileInput.click();
  });

  elements.convertFileInput.addEventListener("change", () => {
    const [file] = Array.from(elements.convertFileInput.files ?? []);
    if (file === undefined) {
      return;
    }
    void openConversionDialogForFile(file)
      .catch((error: unknown) => {
        conversionStatusMessage = error instanceof Error ? error.message : String(error);
        if (!elements.convertDialog.open) {
          elements.convertDialog.showModal();
        }
        renderAll();
      });
  });

  elements.convertModeSelect.addEventListener("change", () => {
    const nextMode = elements.convertModeSelect.value;
    if (nextMode !== "a" && nextMode !== "b" && nextMode !== "c") {
      return;
    }
    applyConversionSettings((settings) => {
      settings.preset = "custom";
      settings.mode = nextMode;
      if (nextMode === "c" && settings.paletteStrategy === "keep-current") {
        settings.paletteStrategy = "optimize-0-3";
      }
    });
  });

  elements.convertPresetSelect.addEventListener("change", () => {
    const preset = elements.convertPresetSelect.value;
    if (preset !== "custom" && preset !== "photo" && preset !== "pixel-art" && preset !== "logo") {
      return;
    }
    if (preset === "custom") {
      applyConversionSettings((settings) => {
        settings.preset = "custom";
      });
      return;
    }
    applyConversionPreset(preset);
  });

  elements.convertFitSelect.addEventListener("change", () => {
    const fitMode = elements.convertFitSelect.value;
    if (fitMode !== "contain" && fitMode !== "cover" && fitMode !== "stretch" && fitMode !== "manual") {
      return;
    }
    applyConversionSettings((settings) => {
      settings.preset = "custom";
      settings.fitMode = fitMode;
    });
  });

  elements.convertPaletteStrategySelect.addEventListener("change", () => {
    const strategy = elements.convertPaletteStrategySelect.value;
    if (strategy !== "keep-current" && strategy !== "optimize-target" && strategy !== "optimize-0-3") {
      return;
    }
    applyConversionSettings((settings) => {
      settings.preset = "custom";
      settings.paletteStrategy = strategy;
    });
  });

  elements.convertDitherSelect.addEventListener("change", () => {
    const dither = elements.convertDitherSelect.value;
    if (dither !== "none" && dither !== "ordered" && dither !== "floyd") {
      return;
    }
    applyConversionSettings((settings) => {
      settings.preset = "custom";
      settings.ditherMode = dither;
    });
  });

  elements.convertMatteInput.addEventListener("change", () => {
    try {
      const matte = parseRgb444(elements.convertMatteInput.value);
      applyConversionSettings((settings) => {
        settings.preset = "custom";
        settings.matteRgb444 = matte;
      });
    } catch (error) {
      conversionStatusMessage = error instanceof Error ? error.message : String(error);
      renderConversionDialog();
    }
  });

  elements.convertManualZoomInput.addEventListener("input", () => {
    applyConversionSettings(
      (settings) => {
        settings.preset = "custom";
        settings.manualZoom = Number.parseInt(elements.convertManualZoomInput.value, 10) / 100;
      },
      "scheduled"
    );
  });

  elements.convertManualPanXInput.addEventListener("input", () => {
    applyConversionSettings(
      (settings) => {
        settings.preset = "custom";
        settings.manualPanX = Number.parseInt(elements.convertManualPanXInput.value, 10) / 100;
      },
      "scheduled"
    );
  });

  elements.convertManualPanYInput.addEventListener("input", () => {
    applyConversionSettings(
      (settings) => {
        settings.preset = "custom";
        settings.manualPanY = Number.parseInt(elements.convertManualPanYInput.value, 10) / 100;
      },
      "scheduled"
    );
  });

  elements.convertResetCropTrigger.addEventListener("click", () => {
    applyConversionSettings((settings) => {
      settings.preset = "custom";
      settings.manualPanX = 0;
      settings.manualPanY = 0;
      settings.manualZoom = 1;
    });
  });

  elements.convertCancelTrigger.addEventListener("click", () => {
    closeConversionDialog();
  });

  elements.convertApplyTrigger.addEventListener("click", () => {
    applyConversionToDraft();
  });

  elements.convertDialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeConversionDialog();
  });

  elements.exportProjectTrigger.addEventListener("click", () => {
    try {
      exportProject();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.exportByteTrigger.addEventListener("click", () => {
    try {
      exportByte();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.paletteApplyTrigger.addEventListener("click", () => {
    try {
      updatePaletteEntry();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.paletteHexInput.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    try {
      updatePaletteEntry();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.applyColorsToAllModeATrigger.addEventListener("click", () => {
    try {
      applyCurrentCellColorsToAll();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.applyColorsToAllModeBTrigger.addEventListener("click", () => {
    try {
      applyCurrentCellColorsToAll();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.leftRail.addEventListener("click", () => {
    leftPanelCollapsed = !leftPanelCollapsed;
    renderAll();
  });

  elements.rightRail.addEventListener("click", () => {
    rightPanelCollapsed = !rightPanelCollapsed;
    renderAll();
  });

  elements.modeSelect.addEventListener("change", () => {
    const nextMode = elements.modeSelect.value;
    if (nextMode !== "a" && nextMode !== "b" && nextMode !== "c") {
      return;
    }
    defaultMode = nextMode;
    activeColorTarget = null;
    if (draft === null) {
      setStatus(`New blank drafts will use ${modeLabel(defaultMode)}.`);
      renderAll();
      return;
    }
    mutateDraft(`Changed the local draft interpretation to ${modeLabel(nextMode)}.`, (currentDraft) => {
      currentDraft.mode = nextMode;
      currentDraft.brush = clampBrush(nextMode, currentDraft.brush);
      currentDraft.selectedX = Math.min(currentDraft.selectedX, logicalWidth(nextMode) - 1);
    });
  });

  elements.zoomSelect.addEventListener("change", () => {
    const nextZoom = Number.parseInt(elements.zoomSelect.value, 10);
    if (nextZoom !== 2 && nextZoom !== 4 && nextZoom !== 8) {
      return;
    }
    defaultZoom = nextZoom;
    if (draft === null) {
      renderAll();
      return;
    }
    draft.zoom = nextZoom;
    setStatus(`Changed editor zoom to x${nextZoom}.`);
    renderAll();
  });

  elements.showPixelGridToggle.addEventListener("change", () => {
    renderAll();
  });

  elements.showCellGridToggle.addEventListener("change", () => {
    renderAll();
  });

  elements.cellFgSelect.addEventListener("change", () => {
    mutateDraft("Updated the selected cell foreground color.", (currentDraft) => {
      setCellFgColor(currentDraft, cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY), Number.parseInt(elements.cellFgSelect.value, 10));
    });
  });

  elements.cellBgSelect.addEventListener("change", () => {
    mutateDraft("Updated the selected cell background color.", (currentDraft) => {
      setCellBgColor(currentDraft, cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY), Number.parseInt(elements.cellBgSelect.value, 10));
    });
  });

  elements.modeBHiSelect.addEventListener("change", () => {
    mutateDraft("Updated the selected Mode B hi color.", (currentDraft) => {
      setCellFgColor(currentDraft, cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY), Number.parseInt(elements.modeBHiSelect.value, 10));
    });
  });

  elements.modeBLoSelect.addEventListener("change", () => {
    mutateDraft("Updated the selected Mode B low color.", (currentDraft) => {
      setCellBgColor(currentDraft, cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY), Number.parseInt(elements.modeBLoSelect.value, 10));
    });
  });

  elements.modeBRamSelect.addEventListener("change", () => {
    mutateDraft("Updated the selected Mode B packed color nibble.", (currentDraft) => {
      setPackedColorNibble(currentDraft.color, cellIndexForPoint(currentDraft.mode, currentDraft.selectedX, currentDraft.selectedY), Number.parseInt(elements.modeBRamSelect.value, 10));
    });
  });

  elements.bgColorSelect.addEventListener("change", () => {
    mutateDraft("Updated VOC_BG_COLOR in the local draft.", (currentDraft) => {
      currentDraft.bgColor = Number.parseInt(elements.bgColorSelect.value, 10) & 0x0f;
    });
  });

  elements.borderColorSelect.addEventListener("change", () => {
    mutateDraft("Updated VOC_BORDER_COLOR in the local draft.", (currentDraft) => {
      currentDraft.borderColor = Number.parseInt(elements.borderColorSelect.value, 10) & 0x0f;
    });
  });

  elements.cellFgSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "a") {
      return;
    }
    setActiveColorTarget("mode-a-fg");
  });

  elements.cellBgSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "a") {
      return;
    }
    setActiveColorTarget("mode-a-bg");
  });

  elements.bgColorSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "b") {
      return;
    }
    setActiveColorTarget("mode-b-bg");
  });

  elements.modeBHiSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "b") {
      return;
    }
    setActiveColorTarget("mode-b-hi");
  });

  elements.modeBLoSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "b") {
      return;
    }
    setActiveColorTarget("mode-b-lo");
  });

  elements.modeBRamSwatch.addEventListener("click", () => {
    if (draft === null || draft.mode !== "b") {
      return;
    }
    setActiveColorTarget("mode-b-ram");
  });

  elements.borderColorSwatch.addEventListener("click", () => {
    if (draft === null) {
      return;
    }
    setActiveColorTarget("border");
  });

  elements.editorCanvas.addEventListener("mousedown", (event) => {
    if (draft === null) {
      return;
    }
    const point = pointFromCanvasEvent(draft, elements.editorCanvas, event);
    if (point === null) {
      return;
    }
    if (event.shiftKey) {
      painting = false;
      selectPointWithoutPainting(point.x, point.y);
      return;
    }
    if (currentTool !== "brush") {
      painting = false;
      applyToolAtPoint(point.x, point.y);
      return;
    }
    painting = true;
    activePaintCode = draft.brush;
    applyToolAtPoint(point.x, point.y);
  });

  elements.editorCanvas.addEventListener("mousemove", (event) => {
    if (!painting || draft === null || currentTool !== "brush") {
      return;
    }
    const point = pointFromCanvasEvent(draft, elements.editorCanvas, event);
    if (point === null) {
      return;
    }
    if (draft.selectedX === point.x && draft.selectedY === point.y && getPixelCode(draft, point.x, point.y) === activePaintCode) {
      return;
    }
    applyBrushPaint(point.x, point.y, `Painted through ${modeLabel(draft.mode)} pixel ${point.x}, ${point.y}.`, false);
  });

  window.addEventListener("mouseup", () => {
    painting = false;
    commitStrokeHistory();
  });

  elements.editorCanvas.addEventListener("mouseleave", () => {
    painting = false;
    commitStrokeHistory();
  });

  elements.writeBitmapTrigger.addEventListener("click", () => {
    try {
      writeBitmap();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.writeScreenTrigger.addEventListener("click", () => {
    try {
      writeScreen();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.writeColorTrigger.addEventListener("click", () => {
    try {
      writeColor();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.writePaletteTrigger.addEventListener("click", () => {
    try {
      writePalette();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.writeAllTrigger.addEventListener("click", () => {
    try {
      writeAll();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  window.addEventListener("keydown", (event) => {
    if (elements.panel.hidden || isEditableElement(event.target)) {
      return;
    }
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        redoDraft();
        return;
      }
      undoDraft();
      return;
    }
    if (modifier && event.key.toLowerCase() === "y") {
      event.preventDefault();
      redoDraft();
      return;
    }
    if (event.key === "Escape" && pendingShapeAnchor !== null) {
      pendingShapeAnchor = null;
      setStatus("Cancelled the pending shape anchor.");
      renderForScope("selection");
    }
  });

  menuBar = createWorkbenchMenuBar(menuHost, {
    getMenus: buildMenus,
    label: "Image Editor menu bar"
  });

  setStatus("Open the Image Editor tab or load a draft to inspect and edit VOC-II images.");
  renderPaletteEntryControls();
  renderAll();

  return {
    refresh() {
      renderAll();
    }
  };
}
